/**
 * 방문요양 급여 — 지원공제(l_jiwon) 항목 선택 정책
 *
 * - l_jiwon_fixed = N: 값이 있는 5개 항목 전부 base
 * - l_jiwon_fixed = Y: jiwon_* 플래그로 선택 항목만 base
 * - 금액은 항상 자동 산출 (수동 입력 없음)
 */

import {
  calcHomeWageSupportDeduction,
  type HomeWageSupportDeductionInput,
  type HomeWageSupportDeductionRates,
  type HomeWageSupportDeductionResult,
} from './homeWageSettingPolicy';

export type HomeWageSupportDeductionItemKey =
  | 'senior'
  | 'bosu'
  | 'welfare'
  | 'severe'
  | 'sunim';

export type HomeWageSupportDeductionItemFlags = Record<
  HomeWageSupportDeductionItemKey,
  boolean
>;

export interface HomeWageSupportDeductionAmounts {
  senior: number;
  bosu: number;
  welfare: number;
  severe: number;
  sunim: number;
}

export interface HomeWageSupportDeductionItemRow {
  key: HomeWageSupportDeductionItemKey;
  label: string;
  amount: number;
  /** 요양보호사만 — 중증가산 */
  caregiverOnly?: boolean;
  /** 센터장/사복만 — 선임수당 */
  staffOnly?: boolean;
}

export const HOME_WAGE_SUPPORT_DEDUCTION_ITEM_DEFS: Omit<
  HomeWageSupportDeductionItemRow,
  'amount'
>[] = [
  { key: 'senior', label: '장기근속수당' },
  { key: 'bosu', label: '보수교육비' },
  { key: 'welfare', label: '처우개선비' },
  { key: 'severe', label: '중증가산수당', caregiverOnly: true },
  { key: 'sunim', label: '선임수당', staffOnly: true },
];

export const HOME_WAGE_SUPPORT_DEDUCTION_ITEM_KEYS: HomeWageSupportDeductionItemKey[] =
  HOME_WAGE_SUPPORT_DEDUCTION_ITEM_DEFS.map((d) => d.key);

export const HOME_WAGE_SUPPORT_DEDUCTION_SELECTIVE_TOOLTIP = [
  '미체크: 금액이 있는 지원공제 대상 수당 전부 반영',
  '체크: 공제할 항목을 직접 선택',
].join('\n');

/** 지원공제 필드 툴팁 (사용자 화면) */
export const HOME_WAGE_SUPPORT_DEDUCTION_FIELD_TOOLTIP = [
  '공단 지원금(장기근속·보수교육·처우개선·중증가산·선임)',
  '× 보험요율 + 공제 대상 ÷ 12',
  '미체크: 금액 있는 항목 전부 / 체크: 항목 직접 선택',
  '60h 미만·대표자·퇴직금 미적용 시 퇴직금분 제외',
].join('\n');

/** 모달·요약 라벨 (코드 용어 base 등 사용 금지) */
export const HOME_WAGE_SUPPORT_DEDUCTION_UI = {
  modalTitle: '지원공제 항목 선택',
  modalDesc: '공제할 항목을 선택하세요. 체크한 수당만 지원공제에 반영됩니다.',
  emptyList: '공제 대상 수당이 없습니다.',
  targetSumLabel: '공제 대상 합계',
  previewLabel: '지원공제(예상)',
  selectiveNote: '선택 항목만 공제',
  emptyFormulaHint:
    '공제 대상 = 장기근속·보수교육·처우개선·중증가산·선임 수당\n해당 수당 없음',
  formulaIntro:
    '지원공제 = 공제 대상 × 보험요율 + 공제 대상 ÷ 12\n해당 수당 없음 → 0원',
} as const;

export function createAllIncludedSupportDeductionFlags(): HomeWageSupportDeductionItemFlags {
  return {
    senior: true,
    bosu: true,
    welfare: true,
    severe: true,
    sunim: true,
  };
}

export function parseSupportDeductionItemFlag(value: unknown): boolean {
  return value === 'Y' || value === true;
}

export function formatSupportDeductionItemFlag(included: boolean): 'Y' | 'N' {
  return included ? 'Y' : 'N';
}

export function loadSupportDeductionItemFlagsFromRow(
  row: Record<string, unknown>
): HomeWageSupportDeductionItemFlags {
  return {
    senior: parseSupportDeductionItemFlag(row.jiwon_senior ?? row.cwhd_jiwon_senior),
    bosu: parseSupportDeductionItemFlag(row.jiwon_bosu ?? row.cwhd_jiwon_bosu),
    welfare: parseSupportDeductionItemFlag(row.jiwon_welfare ?? row.cwhd_jiwon_welfare),
    severe: parseSupportDeductionItemFlag(row.jiwon_severe ?? row.cwhd_jiwon_severe),
    sunim: parseSupportDeductionItemFlag(row.jiwon_sunim ?? row.cwhd_jiwon_sunim),
  };
}

export function mapSupportDeductionItemFlagsToDb(
  flags: HomeWageSupportDeductionItemFlags
): Record<string, 'Y' | 'N'> {
  return {
    jiwon_senior: formatSupportDeductionItemFlag(flags.senior),
    jiwon_bosu: formatSupportDeductionItemFlag(flags.bosu),
    jiwon_welfare: formatSupportDeductionItemFlag(flags.welfare),
    jiwon_severe: formatSupportDeductionItemFlag(flags.severe),
    jiwon_sunim: formatSupportDeductionItemFlag(flags.sunim),
  };
}

/** 사원 유형별 표시 가능한 지원공제 항목 */
export function getSupportDeductionItemsForEmployee(
  isCaregiver: boolean
): Omit<HomeWageSupportDeductionItemRow, 'amount'>[] {
  return HOME_WAGE_SUPPORT_DEDUCTION_ITEM_DEFS.filter((def) => {
    if (def.caregiverOnly && !isCaregiver) return false;
    if (def.staffOnly && isCaregiver) return false;
    return true;
  });
}

export function buildSupportDeductionAmounts(
  form: Partial<HomeWageSupportDeductionAmounts>
): HomeWageSupportDeductionAmounts {
  return {
    senior: Number(form.senior) || 0,
    bosu: Number(form.bosu) || 0,
    welfare: Number(form.welfare) || 0,
    severe: Number(form.severe) || 0,
    sunim: Number(form.sunim) || 0,
  };
}

/** 값 > 0 인 항목만 모달·base 후보 */
export function listSupportDeductionItemRows(
  amounts: HomeWageSupportDeductionAmounts,
  isCaregiver: boolean
): HomeWageSupportDeductionItemRow[] {
  return getSupportDeductionItemsForEmployee(isCaregiver)
    .map((def) => ({
      ...def,
      amount: amounts[def.key] || 0,
    }))
    .filter((row) => row.amount > 0);
}

/** 자동 모드: 값 있는 항목 전부 포함 플래그 */
export function resolveAutoSupportDeductionFlags(
  amounts: HomeWageSupportDeductionAmounts,
  isCaregiver: boolean
): HomeWageSupportDeductionItemFlags {
  const flags = createAllIncludedSupportDeductionFlags();
  for (const def of HOME_WAGE_SUPPORT_DEDUCTION_ITEM_DEFS) {
    if (def.caregiverOnly && !isCaregiver) flags[def.key] = false;
    else if (def.staffOnly && isCaregiver) flags[def.key] = false;
    else flags[def.key] = (amounts[def.key] || 0) > 0;
  }
  return flags;
}

/** 선택 모드에서 base에 포함할 금액 합산 */
export function resolveSupportDeductionBaseParts(
  amounts: HomeWageSupportDeductionAmounts,
  flags: HomeWageSupportDeductionItemFlags,
  selectiveMode: boolean,
  isCaregiver: boolean
): HomeWageSupportDeductionAmounts {
  const effectiveFlags = selectiveMode
    ? flags
    : resolveAutoSupportDeductionFlags(amounts, isCaregiver);

  return {
    senior: effectiveFlags.senior ? amounts.senior : 0,
    bosu: effectiveFlags.bosu ? amounts.bosu : 0,
    welfare: effectiveFlags.welfare ? amounts.welfare : 0,
    severe: effectiveFlags.severe ? amounts.severe : 0,
    sunim: effectiveFlags.sunim ? amounts.sunim : 0,
  };
}

export function calcSupportDeductionBaseTotal(parts: HomeWageSupportDeductionAmounts): number {
  return Math.max(
    0,
    parts.senior + parts.bosu + parts.welfare + parts.severe + parts.sunim
  );
}

export interface HomeWageSupportDeductionCalcContext {
  amounts: HomeWageSupportDeductionAmounts;
  flags: HomeWageSupportDeductionItemFlags;
  selectiveMode: boolean;
  isCaregiver: boolean;
  rates: HomeWageSupportDeductionRates;
  yeonDeduct?: boolean;
  goDeduct?: boolean;
  includeRetirement?: boolean;
}

export function calcHomeWageSupportDeductionWithItems(
  ctx: HomeWageSupportDeductionCalcContext
): HomeWageSupportDeductionResult & { parts: HomeWageSupportDeductionAmounts; base: number } {
  const parts = resolveSupportDeductionBaseParts(
    ctx.amounts,
    ctx.flags,
    ctx.selectiveMode,
    ctx.isCaregiver
  );

  const input: HomeWageSupportDeductionInput = {
    senior: parts.senior,
    bosu: parts.bosu,
    welfare: parts.welfare,
    severe: parts.severe,
    sunim: parts.sunim,
    rates: ctx.rates,
    yeonDeduct: ctx.yeonDeduct,
    goDeduct: ctx.goDeduct,
    includeRetirement: ctx.includeRetirement,
  };

  const result = calcHomeWageSupportDeduction(input);
  return { ...result, parts, base: result.base };
}

/** 툴팁·모달용 공제 대상 구성 요약 */
export function formatSupportDeductionBasePartsText(
  parts: HomeWageSupportDeductionAmounts,
  formatMoney: (n: number) => string
): string {
  const labelByKey = Object.fromEntries(
    HOME_WAGE_SUPPORT_DEDUCTION_ITEM_DEFS.map((d) => [d.key, d.label])
  ) as Record<HomeWageSupportDeductionItemKey, string>;
  const chunks: string[] = [];
  for (const key of HOME_WAGE_SUPPORT_DEDUCTION_ITEM_KEYS) {
    const val = parts[key] || 0;
    if (val > 0) chunks.push(`${labelByKey[key]} ${formatMoney(val)}`);
  }
  return chunks.join(' + ');
}

/** 사용자 화면 — 공제 대상 한 줄 요약 */
export function formatSupportDeductionTargetSummaryLine(
  parts: HomeWageSupportDeductionAmounts,
  total: number,
  formatMoney: (n: number) => string
): string {
  const detail = formatSupportDeductionBasePartsText(parts, formatMoney);
  if (detail) {
    return `공제 대상 = ${detail} = ${formatMoney(total)}원`;
  }
  return `공제 대상 = ${formatMoney(total)}원`;
}

/** 사용자 화면 — 모달 하단 구성 내역 */
export function formatSupportDeductionTargetDetailLine(
  parts: HomeWageSupportDeductionAmounts,
  formatMoney: (n: number) => string
): string {
  const detail = formatSupportDeductionBasePartsText(parts, formatMoney);
  return detail ? `공제 대상: ${detail}` : '';
}
